/**
 * Outbound email senders — the company SMTP configs (Integrations → Email &
 * Inboxes → Outbound) and the logged-in user's personal Gmail (formerly only
 * on the Profile page).
 *
 * These live next to the portal lead inboxes on one screen because a Gmail or
 * Zoho app password is not scoped per protocol: the same password sends over
 * SMTP and reads over IMAP. Builders kept being asked for "an app password"
 * twice without being told it was the same one.
 */

export const COMPANY_SMTP_STATUSES_QUERY_KEY = 'companySmtpStatuses';
export const MY_SMTP_STATUS_QUERY_KEY = ['mySmtpStatus'];

/**
 * Whether GET /company/smtp/:id says the company can send.
 *
 * The endpoint's historical contract is "a body means configured, a failed
 * request means not" — `checkCompanySMTP` returns null on any error — and the
 * old Mail Companies page read it exactly that way. An explicit `connected`
 * flag, if the server ever sends one, wins over that.
 */
export function isCompanySmtpConnected(status) {
  if (!status) return false;
  if (typeof status.connected === 'boolean') return status.connected;
  if (status.data && typeof status.data.connected === 'boolean') return status.data.connected;
  return true;
}

function firstAddress(...candidates) {
  for (const value of candidates) {
    if (typeof value === 'string' && value.includes('@')) return value.trim();
  }
  return null;
}

/** The From address a company sends as, when the status response carries one. */
export function companySmtpAddress(status) {
  if (!status || typeof status !== 'object') return null;
  const body = status.data && typeof status.data === 'object' ? status.data : status;
  return firstAddress(
    body.smtp_from_email,
    body.fromEmail,
    body.from_email,
    body.smtp_user,
    body.user
  );
}

/**
 * A masked address ("um***@gmail.com") is shown to the user as-is, but it
 * cannot be compared with anything, so matching treats it as unknown.
 */
export function isMaskedAddress(address) {
  return typeof address === 'string' && address.includes('*');
}

function normalise(address) {
  return String(address ?? '')
    .trim()
    .toLowerCase();
}

/**
 * The sender, if any, already using this mailbox address.
 *
 * `senders` is `[{ address, name, kind }]`. Used by the Add inbox form to say
 * "you already saved an app password for this address", which is the moment
 * the duplication is felt.
 */
export function findSenderForAddress(senders, address) {
  const wanted = normalise(address);
  if (!wanted.includes('@')) return null;
  return (
    (senders ?? []).find(
      (sender) =>
        sender?.address && !isMaskedAddress(sender.address) && normalise(sender.address) === wanted
    ) ?? null
  );
}

/** "3 of 4 connected" style line for the Integrations card and the hub header. */
export function sendingSummary({ companyTotal = 0, companyConnected = 0, personalConnected }) {
  const connected = companyConnected + (personalConnected ? 1 : 0);
  const total = companyTotal + 1;
  if (connected === 0) return 'No sender connected';
  return `${connected} of ${total} sender${total === 1 ? '' : 's'} connected`;
}

/** Same idea for the lead inboxes. */
export function receivingSummary(mailboxes) {
  const list = mailboxes ?? [];
  if (list.length === 0) return 'No inbox connected';
  const active = list.filter((mailbox) => mailbox.isActive).length;
  const noun = list.length === 1 ? 'inbox' : 'inboxes';
  if (active === list.length) return `${list.length} ${noun} connected`;
  return `${list.length} ${noun} · ${active} being read`;
}

export const EMAIL_HUB_PATH = '/builder/integration/email-leads';
export const EMAIL_HUB_TABS = ['leads', 'outbound'];

/** `?tab=` value → a tab that exists; anything else lands on lead ingestion. */
export function emailHubTab(value) {
  return EMAIL_HUB_TABS.includes(value) ? value : 'leads';
}
